import { Prompt } from "@/api/prompts-types";
import { RoleName } from "@/api/users-types";
import { CreatePrompt } from "@/components/prompts/CreatePrompt";
import { ApiErrorAlert } from "@/components/ui/ApiErrorAlert";
import { Link } from "@/components/ui/Link";
import { NoPermissions } from "@/components/ui/NoPermissions";
import { useHasRole } from "@/hooks/use-has-role";
import { useRowCount } from "@/hooks/use-row-count";
import { usePrompts } from "@/queries/prompts/use-prompts";
import { usePromptsDatagridStore } from "@/stores/prompts-datagrid-store";
import {
  promptTypeOptions,
  storyTypeOptions,
  timestampColumns,
} from "@/utils/datagrid-utils";
import { getLatest } from "@/utils/get-latest";
import { Stack } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";

const columns: GridColDef<Prompt>[] = [
  {
    field: "id",
    headerName: "ID",
    width: 120,
    renderCell: (params) => (
      <Link to={`/prompts/${params.row.id}`}>{params.row.id}</Link>
    ),
  },
  {
    field: "name",
    headerName: "Name",
    width: 180,
  },
  {
    field: "storyType",
    headerName: "Story Type",
    width: 120,
    type: "singleSelect",
    valueOptions: storyTypeOptions,
  },
  {
    field: "type",
    headerName: "Prompt Type",
    width: 120,
    type: "singleSelect",
    valueOptions: promptTypeOptions,
  },
  {
    field: "revisions",
    headerName: "Revision",
    width: 120,
    type: "number",
    valueGetter: (_, row) => getLatest(row.revisions)?.revision || 0,
    align: "left",
    headerAlign: "left",
  },
  {
    field: "revisionNote",
    headerName: "Revision Note",
    width: 240,
    valueGetter: (_, prompt) => getLatest(prompt.revisions)?.revisionNote || "",
  },
  {
    field: "description",
    headerName: "Description",
    width: 240,
  },
  ...timestampColumns<Prompt>(),
];

export function PromptListPage() {
  const {
    paginationModel,
    sortModel,
    filterModel,
    setPaginationModel,
    setSortModel,
    setFilterModel,
  } = usePromptsDatagridStore();

  const { data, isLoading, error } = usePrompts(paginationModel);

  const rowCount = useRowCount(data);

  const canCreate = useHasRole(RoleName.promptEngineer);
  const canView = useHasRole([
    RoleName.promptEngineer,
    RoleName.episodeManager,
  ]);

  if (!canView) {
    return <NoPermissions />;
  }

  return (
    <Stack gap={2} mb={2}>
      <ApiErrorAlert error={error} />
      <DataGrid
        rowHeight={46}
        columnHeaderHeight={46}
        columns={columns}
        filterModel={filterModel}
        loading={isLoading}
        pageSizeOptions={[25, 50, 100]}
        paginationMode={"server"}
        paginationModel={paginationModel}
        rowCount={rowCount}
        rows={data?.items}
        sortModel={sortModel}
        onPaginationModelChange={setPaginationModel}
        onFilterModelChange={setFilterModel}
        onSortModelChange={setSortModel}
      />
      {canCreate && (
        <Stack justifyContent={"flex-end"} direction={"row"} gap={1}>
          <CreatePrompt />
        </Stack>
      )}
    </Stack>
  );
}
